import { Stack, createStyles } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';
import { useState } from 'react';

import { Text } from '~/domains/design-system/Text';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import type { APIField } from '../../types/main';
import { FieldMeta } from '../common/FieldMeta';
import type { FieldQuery } from '../../../context/types/store';

export type APIFieldGroupType = BaseInterativeField & {
  type: 'FieldGroup';
  fields: APIField[];
};

export type FieldGroupType = BaseInterativeField & {
  type: 'FieldGroup';
  learnHowLabel?: string;
  fields: string[];
  info?: {
    title: string;
    data: {
      setting: string;
      template?: string;
      description: string;
    }[];
  };
};

const useStyles = createStyles((theme) => ({
  card: {
    minHeight: 38,
    width: '100%',
    position: 'relative',
    borderRadius: theme.radius.sm,
    '&.withBorder': {
      paddingLeft: theme.other.spacing._12,
      paddingRight: theme.other.spacing._12,
      paddingTop: theme.other.spacing._8,
      paddingBottom: theme.other.spacing._8,
      background: theme.white,
      border: `1px solid ${theme.colors.gray2[1]}`,
    },

    '&.withPadding': {
      padding: theme.other.spacing._24,
      background: theme.white,
    },
  },
  info: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  description: {
    '& b': {
      fontWeight: 'bold',
    },
  },
  overlay: {
    borderRadius: theme.radius.md,
    background: theme.white,
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    opacity: 0.55,
  },
  tooltip: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'not-allowed',
  },
  tooltipLabel: {
    color: theme.colors.gray2[9],
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    paddingLeft: theme.other.spacing._16,
    paddingRight: theme.other.spacing._16,
    background: theme.colors.gray2[0],
    border: `1px solid ${theme.colors.gray2[2]}`,
    borderRadius: theme.radius.sm,
  },
}));

function FieldGroupElement({
  field,
  errors,
  sectionKey,
  isTouched,
  FieldSwitch,
  path = [],
  propagateFieldLabelToParent,
}: StaticFieldProps<FieldGroupType>) {
  const [hover, setHover] = useState(false);
  const { classes, cx } = useStyles();
  return (
    <div
      className={cx(classes.card, {
        withBorder: field?.withBorder,
        withPadding: field?.withPadding,
      })}
      onBlur={() => setHover(false)}
      onFocus={() => setHover(!hover)}
      onClick={() => setHover(!hover)}
      onKeyDown={() => setHover(!hover)}
      onMouseLeave={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      role="button"
      tabIndex={0}
    >
      <div>
        {(field.label || field.description) && (
          <FieldMeta info={field?.info} field={field} errors={errors} isTouched={isTouched} />
        )}
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={10}>
          {field.fields.map((key) => (
            <FieldSwitch
              key={key}
              fieldKey={key}
              sectionKey={sectionKey}
              path={path}
              propagateFieldLabelToParent={propagateFieldLabelToParent}
            />
          ))}
        </Stack>
      </div>

      {field?.locked ? (
        <>
          <div className={classes.overlay} />
          {hover ? (
            <div className={classes.tooltip}>
              <Text className={classes.tooltipLabel} variant="body-medium">
                {field?.locked}
              </Text>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const Descriptor: FieldDescriptor<APIFieldGroupType, FieldGroupType> = {
  FieldComponent: FieldGroupElement,
  name: 'FieldGroup',
  parseFieldResponse: ({ field, fieldKey, parse }) => {
    const fields = field.fields
      .map((childField) => parse(childField, `${fieldKey}.`))
      .filter(Boolean);
    const normalizedField: FieldQuery = {
      key: fieldKey,
      type: field.type,
      data: {
        ...field,
        fields: fields.map((f) => f[0].key),
      },
    };

    return [normalizedField, fields];
  },
  createValidationSchema: (field, create) => {
    let fieldSchema: ZodTypeAny = z.object(
      field.fields.reduce((acc, fieldKey) => {
        const result = create(fieldKey);
        if (result) {
          if ('field' in result) {
            const [key, schema] = result.field;
            acc[key] = schema;
          } else {
            result.fields.forEach(([key, schema]) => {
              acc[key] = schema;
            });
          }
        }

        return acc;
      }, {})
    );

    if (field.validation?.isOptional) {
      fieldSchema = fieldSchema.optional();
    }

    return fieldSchema;
  },
};

export default Descriptor;
